/**
 * ClientContextHeader — P1, the signature element: the client, stated once,
 * on every settings screen. Grounding: ACTIONABLE-PLAN §0 P1 row +
 * consultation-log FABLE-P1-PARTNER-TWT.
 *
 * Rules that make it P1 and not a generic page header:
 *  - NO status pills here — the strip directly below is the one status
 *    surface (partner amendment: name + domain ARE the signature).
 *  - The name is a real heading (h1) in the serif face with optical sizing
 *    (`font-optical-sizing: auto`); the domain is a real identifier element
 *    (<code>) in mono at text-ink-2 — secondary, never faint.
 *  - Long content: the name wraps at most 2 lines at word boundaries
 *    (line-clamp-2 + break-words + hyphens-none), the full value survives on
 *    the title attribute, and we NEVER scale/compress the type. Height stays
 *    stable because every line-height in the header is a multiple of one
 *    8px increment (name leading-8, meta row leading-6).
 *  - The plan chip and the brand-accent chip are garnish: quiet paper chip
 *    for the plan, and a small swatch that reads var(--color-coral), ringed with border-warm-border
 *    so pale derived accents stay visible, with an accessible label.
 *  - Unlinked deep-link state gets the honest amber variant: the domain as
 *    the identity, the "Not linked to a workspace" chip, and exactly ONE
 *    coral recovery action.
 *
 * Every user-visible string comes from copyTokens (P4).
 */

import { Link } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { resolveProductPackage } from '@/types/auth';
import { COPY } from '@/lib/copyTokens';

export function ClientContextHeader() {
  const selection = useWorkspaceStore((s) => s.selection);

  if (!selection) return null; // the layout renders the empty state instead

  if (selection.kind === 'tenant') {
    // Unlinked deep-link state: honest, recoverable, never a fake workspace.
    return (
      <header data-testid="client-context-header" className="mb-4">
        <h1 className="font-mono text-2xl leading-8 text-ink">{selection.tenantId}</h1>
        <div className="mt-2 flex items-center gap-2 leading-6">
          <span className="inline-flex items-center rounded-full bg-amber-light px-2.5 py-0.5 text-xs font-medium text-amber-dark">
            {COPY.unlinkedChip}
          </span>
          <Link
            to="/create-workspace"
            className="text-xs font-medium text-coral hover:text-coral-hover"
          >
            {COPY.addEntity}
          </Link>
        </div>
      </header>
    );
  }

  const { workspace } = selection;
  const plan = COPY.plans[resolveProductPackage(workspace.product_package)];

  return (
    <header data-testid="client-context-header" className="mb-4">
      <h1
        title={workspace.name}
        className="font-display text-2xl font-medium leading-8 text-ink line-clamp-2 break-words hyphens-none [font-optical-sizing:auto]"
      >
        {workspace.name}
      </h1>
      <div className="mt-2 flex items-center gap-2 leading-6">
        {workspace.website ? (
          <code title={workspace.website} className="font-mono text-sm text-ink-2">
            {workspace.website}
          </code>
        ) : null}
        <span className="inline-flex items-center rounded-full border border-warm-border bg-paper-2 px-2.5 py-0.5 text-xs font-medium text-ink-2">
          {plan}
        </span>
        <span
          role="img"
          aria-label={COPY.brandAccent}
          title={COPY.brandAccent}
          className="inline-block size-4 rounded-full border border-warm-border"
          style={{ backgroundColor: 'var(--color-coral)' }}
        />
      </div>
    </header>
  );
}
